import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session as SupabaseSession, User } from "@supabase/supabase-js";
import { supabase, isSupabaseEnabled } from "./supabaseClient";

// Volunteer auth/session context — separate from lib/session.tsx (the
// scheduler/admin session). Same Supabase project, same auth.users
// table, same signInWithPassword/signUp calls, but a different
// application-level role backed by its own table
// (volunteer_accounts, not users) and its own RLS. Kept as a fully
// separate provider rather than extending Session so the existing
// admin app (dozens of components read useSession()) is untouched.

export interface VolunteerSession {
  userId: string;
  email: string;
  displayName: string;
}

const VolunteerSessionContext = createContext<VolunteerSession | null>(null);
const VolunteerJwtContext = createContext<string | null>(null);

export function useVolunteerSession(): VolunteerSession {
  const s = useContext(VolunteerSessionContext);
  if (!s) {
    throw new Error(
      "useVolunteerSession must be used inside <VolunteerSessionProvider>.",
    );
  }
  return s;
}

// Current Supabase access token for the signed-in volunteer. lib/api.ts's
// apiGet/apiPost already attach whichever session is active via
// supabase.auth.getSession() directly, so this is mostly for callers
// that want to branch on "is a volunteer JWT present" without a round
// trip.
export function useVolunteerJwt(): string | null {
  return useContext(VolunteerJwtContext);
}

type BootState =
  | { kind: "loading" }
  | { kind: "signed-out" }
  // Signed in with a valid Supabase session, but no volunteer_accounts
  // row for this identity — e.g. a scheduler's own login landed on a
  // volunteer route, or the signup trigger hasn't run yet.
  | { kind: "not-a-volunteer" }
  | { kind: "ready"; session: VolunteerSession; jwt: string };

export function VolunteerSessionProvider({
  children,
  fallback,
  signedOutFallback,
  notVolunteerFallback,
}: {
  children: ReactNode;
  fallback: ReactNode;
  signedOutFallback: ReactNode;
  notVolunteerFallback: ReactNode;
}) {
  const [state, setState] = useState<BootState>(() =>
    isSupabaseEnabled ? { kind: "loading" } : { kind: "signed-out" },
  );

  useEffect(() => {
    if (!isSupabaseEnabled || !supabase) return;

    let cancelled = false;

    async function applySession(supaSession: SupabaseSession | null) {
      if (cancelled) return;
      if (!supaSession) {
        setState({ kind: "signed-out" });
        return;
      }
      const session = await hydrateVolunteerSession(supaSession.user);
      if (cancelled) return;
      if (!session) {
        setState({ kind: "not-a-volunteer" });
        return;
      }
      setState({ kind: "ready", session, jwt: supaSession.access_token });
    }

    supabase.auth.getSession().then(({ data }) => applySession(data.session));

    const { data: sub } = supabase.auth.onAuthStateChange((_event, supaSession) => {
      void applySession(supaSession);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state.kind === "loading") return <>{fallback}</>;
  if (state.kind === "signed-out") return <>{signedOutFallback}</>;
  if (state.kind === "not-a-volunteer") return <>{notVolunteerFallback}</>;

  return (
    <VolunteerSessionContext.Provider value={state.session}>
      <VolunteerJwtContext.Provider value={state.jwt}>{children}</VolunteerJwtContext.Provider>
    </VolunteerSessionContext.Provider>
  );
}

interface VolunteerAccountRow {
  id: string;
  email: string;
  display_name: string | null;
}

async function hydrateVolunteerSession(user: User): Promise<VolunteerSession | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("volunteer_accounts")
    .select("id, email, display_name")
    .eq("id", user.id)
    .maybeSingle<VolunteerAccountRow>();
  if (error || !data) return null;
  return {
    userId: data.id,
    email: data.email,
    // Same rule as the scheduler session: no name beats a mangled
    // email-local-part guess.
    displayName: data.display_name?.trim() || "",
  };
}
